import { NameIdOption } from "../types/commonTypes";
import { TalentStatus } from "../../features/talents/types/common";
import { AutocompleteType } from "../types/autoComplete";
import isEmpty from "./isEmpty";

export const trueFalseAutocompleteOptions: AutocompleteType[] = [
  { id: "true", name: "yes" },
  { id: "false", name: "no" },
];

export const stringToList = (
  string?: string,
  delimiter: string = ",",
): string[] =>
  (string ?? "")
    .split(delimiter)
    .map((s) => s.trim())
    .filter(Boolean);

export function joinNamesByIds(ids: string[], options: NameIdOption[]): string {
  return ids
    .map((id) => options.find((opt) => opt.id === id)?.name || id)
    .join(", ");
}

export const isTalentActive = (status?: TalentStatus): boolean =>
  status === TalentStatus.ACTIVE;

/**
 * Resolves localized display names from a localization map by ID.
 * Accepts a single ID string, a single { id } object, or an array of { id } objects.
 * Falls back to the raw ID if a translation is not found.
 * Usage example: localizeNames(localized.workTitle, entry.workTitles)
 */
export function localizeNames(
  localizationMap: Record<string, string> | undefined,
  items: string | { id: string } | { id: string }[],
  separator = ", ",
): string {
  const ids = Array.isArray(items)
    ? items.map((item) => item.id)
    : [typeof items === "string" ? items : items.id];
  return ids.map((id) => localizationMap?.[id] ?? id).join(separator);
}

export const compareByFieldFi =
  <T,>(getField: (item: T) => string) =>
  (a: T, b: T) =>
    getField(a).localeCompare(getField(b), "fi");

export function sortByName<T extends { firstName: string; lastName: string }>(
  list: T[],
  key: "firstName" | "lastName" = "lastName",
): T[] {
  if (isEmpty(list)) return [];
  const otherKey = key === "firstName" ? "lastName" : "firstName";
  // Sort by the specified key, and use the other key as a tiebreaker
  return [...list].sort((a, b) => {
    const primaryComparison = a[key].localeCompare(b[key], "fi");
    if (primaryComparison !== 0) return primaryComparison;
    return a[otherKey].localeCompare(b[otherKey], "fi");
  });
}

export function getName<
  T extends {
    firstName?: string;
    lastName?: string;
    talentFirstName?: string;
    talentLastName?: string;
  },
>(
  item: T,
  format: "firstNameFirst" | "lastNameFirst" = "lastNameFirst",
): string {
  const firstName = item.firstName || item.talentFirstName || "";
  const lastName = item.lastName || item.talentLastName || "";
  return format === "lastNameFirst"
    ? `${lastName} ${firstName}`
    : `${firstName} ${lastName}`;
}
